'use client';

import { CalendarIcon, ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Button } from '@registry/new-york/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@registry/new-york/ui/popover';
import { Column } from '@tanstack/react-table';
import clsx from 'clsx';
import { useState } from 'react';

import { CalendarPicker, DateRangeValue } from './CalendarPicker/CalendarPicker';
import styles from './DataTableDateRangeColumnHeader.module.scss';

interface DataTableDateRangeColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  store?: any;
}

const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

const PRESETS: { label: string; range: () => DateRangeValue }[] = [
  { label: 'Today', range: () => ({ after: startOfDay(new Date()), before: endOfDay(new Date()) }) },
  {
    label: 'Yesterday',
    range: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return { after: startOfDay(d), before: endOfDay(d) };
    },
  },
  {
    label: 'Last 7 days',
    range: () => {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      return { after: startOfDay(d), before: endOfDay(new Date()) };
    },
  },
  {
    label: 'Last 30 days',
    range: () => {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return { after: startOfDay(d), before: endOfDay(new Date()) };
    },
  },
  {
    label: 'This month',
    range: () => {
      const now = new Date();
      return { after: new Date(now.getFullYear(), now.getMonth(), 1), before: endOfDay(now) };
    },
  },
  {
    label: 'Last month',
    range: () => {
      const now = new Date();
      return {
        after: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        before: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
];

export const DataTableDateRangeColumnHeader = <TData, TValue>({
  column,
  title,
  className,
  store,
}: DataTableDateRangeColumnHeaderProps<TData, TValue>) => {
  const [open, setOpen] = useState(false);

  const applied = (column.getFilterValue() as DateRangeValue | undefined) ?? {};
  const isFiltered = Boolean(applied.after || applied.before);

  const [temp, setTemp] = useState<DateRangeValue>({});

  const handleOpen = (next: boolean) => {
    if (next) setTemp({ ...applied });
    setOpen(next);
  };

  const apply = () => {
    const hasValue = temp.after || temp.before;
    store?.setCurrentPage(1);
    column.setFilterValue(hasValue ? temp : undefined);
    setOpen(false);
  };

  const cancel = () => {
    setTemp({ ...applied });
    setOpen(false);
  };

  const clearInline = (e: React.MouseEvent) => {
    e.stopPropagation();
    store?.setCurrentPage(1);
    column.setFilterValue(undefined);
  };

  const activePreset = PRESETS.find(p => {
    const r = p.range();
    return (
      r.after?.toDateString() === temp.after?.toDateString() && r.before?.toDateString() === temp.before?.toDateString()
    );
  });

  const summaryText = () => {
    if (temp.after && temp.before) return `${fmt(temp.after)} – ${fmt(temp.before)}`;
    if (temp.after) return `From ${fmt(temp.after)} — pick an end date`;
    if (temp.before) return `To ${fmt(temp.before)} — pick a start date`;
    return null;
  };

  const badgeText = () => {
    if (applied.after && applied.before) return `${fmt(applied.after)} → ${fmt(applied.before)}`;
    if (applied.after) return `from ${fmt(applied.after)}`;
    return `to ${fmt(applied.before)}`;
  };

  return (
    <div className={clsx(styles.header, className)}>
      <Popover
        open={open}
        onOpenChange={handleOpen}
      >
        <PopoverTrigger asChild>
          <div className={clsx(styles.triggerWrap, isFiltered && styles.triggerWrapActive)}>
            <button
              type="button"
              className={clsx(styles.trigger, isFiltered && styles.triggerFiltered)}
            >
              <CalendarIcon className={styles.calIcon} />
              <span className={styles.triggerTitle}>{title}</span>
              {isFiltered && <span className={styles.badge}>{badgeText()}</span>}
              <ChevronDownIcon className={styles.chevron} />
            </button>
            {isFiltered && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={clearInline}
                aria-label="Clear"
              >
                <Cross2Icon />
              </button>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent
          className={styles.popover}
          align="start"
          sideOffset={6}
        >
          <div className={styles.popoverInner}>
            <div className={styles.presets}>
              {PRESETS.map(p => (
                <button
                  key={p.label}
                  type="button"
                  className={clsx(styles.chip, activePreset?.label === p.label && styles.chipActive)}
                  onClick={() => setTemp(p.range())}
                >
                  {p.label}
                </button>
              ))}
              {(temp.after || temp.before) && (
                <button
                  type="button"
                  className={clsx(styles.chip, styles.chipReset)}
                  onClick={() => setTemp({})}
                >
                  Reset
                </button>
              )}
            </div>

            <CalendarPicker
              mode="range"
              value={temp}
              onChange={setTemp}
            />

            <div className={styles.summary}>
              {summaryText() ? (
                <span className={styles.summaryText}>{summaryText()}</span>
              ) : (
                <span className={styles.summaryPlaceholder}>Select a date or range</span>
              )}
            </div>

            <div className={styles.footer}>
              <Button
                variant="ghost"
                size="sm"
                className={styles.cancelBtn}
                onClick={cancel}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className={styles.applyBtn}
                onClick={apply}
                disabled={!temp.after && !temp.before}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
